import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { MEMBERSHIP_STATUS_LABELS, type AnnualMembership } from "@nh/shared";
import { Badge, Button, type BadgeVariant } from "@/shared/ui/core";
import { KpiCard } from "@/features/admin/components";
import { formatOrderDate } from "@/features/admin/orders/format";

/**
 * ═══════════════════════════════════════════════════════════════════
 * La MEMBRESÍA ANUAL (Anahí, 14-sep-2026)
 *
 * «Es anual: te da acceso a tu backoffice, la que te va a permitir
 * ganar dinero. Se paga cada año. Sin ella no cobras comisiones. Si
 * compras un paquete se te regala.»
 *
 * Es OTRO reloj, aparte del de recompra (30 días, puntos): este dura
 * un año y lo renueva cualquier membresía, paquete o franquicia. Aquí
 * se enseña con sus fechas, y con el llamado a renovar cuando toca.
 * ═══════════════════════════════════════════════════════════════════
 */
const VARIANTS: Record<AnnualMembership["status"], BadgeVariant> = {
  none: "neutral",
  active: "success",
  expiring: "warning",
  expired: "error",
};

const SOURCE_LABELS: Record<NonNullable<AnnualMembership["source"]>, string> = {
  membership: "Compraste tu membresía",
  package: "Te la regaló tu paquete",
  franchise: "Vino con tu franquicia",
  manual: "La activó el equipo",
};

export function AnnualMembershipCard({ annual }: { annual: AnnualMembership }) {
  const { t } = useTranslation();
  const needsRenewal = annual.status !== "active";

  return (
    <section className="relative rounded-3xl border border-ds-border bg-white p-5 shadow-ds-float">
      <div className="relative grid gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-ds-lavender-bg text-ds-violet-pop">
              <ShieldCheck className="h-4 w-4" />
            </span>
            <h2 className="text-[15px] font-semibold text-ds-ink">{t("Membresía anual")}</h2>
            <Badge variant={VARIANTS[annual.status]} size="sm" dot>
              {t(MEMBERSHIP_STATUS_LABELS[annual.status])}
            </Badge>
          </div>
          {needsRenewal && (
            <Button as={Link} to="/paquetes" size="sm" rightIcon={<ArrowRight />}>
              {annual.status === "none" ? t("Activar mi membresía") : t("Renovar mi membresía")}
            </Button>
          )}
        </div>

        <p className="text-[13px] leading-relaxed text-ds-ink-3">
          {t(
            "Tu membresía anual es la que te da acceso a cobrar las comisiones de tu red. Se paga cada año: cualquier membresía o paquete la renueva por 12 meses más.",
          )}
        </p>

        <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">
          <KpiCard
            icon={ShieldCheck}
            item="estado-membresia"
            label={t("Vence el")}
            value={annual.validUntil ? formatOrderDate(annual.validUntil) : "—"}
            hint={annual.source ? t(SOURCE_LABELS[annual.source]) : undefined}
          />
          <KpiCard
            icon={ShieldCheck}
            item="reloj"
            label={t("Días restantes")}
            value={String(annual.remainingDays)}
            hint={
              annual.status === "expiring"
                ? t("Renueva antes de que venza para no dejar de cobrar")
                : annual.status === "expired"
                  ? t("Mientras no renueves, no cobras comisiones")
                  : undefined
            }
          />
          <KpiCard
            icon={ShieldCheck}
            item="inicio-ciclo"
            label={t("Desde")}
            value={annual.startedAt ? formatOrderDate(annual.startedAt) : "—"}
          />
        </div>
      </div>
    </section>
  );
}
